import React, {
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Placeholder } from "./Placeholder";
import { getStickerProductImageUrl } from "./StickerTabs";
import {
  OWNED_COLLECTION_PAGE_SIZE,
  OwnedCollectionItem,
  filterOwnedCollection,
  getOwnedCollectionRowKeys,
  paginateOwnedCollection,
  sortOwnedCollectionOnce,
} from "../utils/myCollectionSearch";
import {
  StickerSortBy,
  normalizeStickerSortBy,
} from "../utils/stickerTabsSearch";
import { LoungeSignInPrompt } from "./LoungeSignInPrompt";
import {
  formatLoungeCreditBalance,
  isKnownNumericCreditBalance,
} from "../utils/loungeSession";

type MyCollectionResultsProps = {
  items: OwnedCollectionItem[];
  status: string;
  creditBalance?: unknown;
  sessionExpired?: boolean;
  onGift: (id: OwnedCollectionItem["id"]) => void;
};

const CollectionStatus = ({ item }: { item: OwnedCollectionItem }) => {
  switch (String(item.status ?? "")) {
    case "1":
      return <>Owner</>;
    case "2":
      return <>Gift from {item.user?.user_name}</>;
    case "3":
      return <>Traded</>;
    default:
      return null;
  }
};

const CollectionRow = memo(function CollectionRow({
  item,
  onGift,
}: {
  item: OwnedCollectionItem;
  onGift: (id: OwnedCollectionItem["id"]) => void;
}) {
  const stickerName = String(item.product_name || "sticker");
  return (
    <tr className="in-sec">
      <th scope="row">
        <div className="tab-img">
          <img
            src={getStickerProductImageUrl(String(item.product_image || ""))}
            className="img-fluid"
            alt={stickerName}
            width={72}
            height={72}
            loading="lazy"
            decoding="async"
          />
        </div>
      </th>
      <td style={{ fontWeight: "500" }}>{item.product_name}</td>
      <td style={{ fontWeight: "500" }}>{item.product_description}</td>
      <td style={{ fontWeight: "500" }}>
        <CollectionStatus item={item} />
      </td>
      <td>
        <div className="buy-btn">
          <button
            type="button"
            onClick={() => onGift(item.id)}
            style={{
              background: "#a0b7e9",
              borderRadius: "0.5rem",
              padding: "0.5rem",
              fontSize: "1rem",
              textDecoration: "none",
              color: "#fff",
              border: 0,
            }}
            className="buy-b"
          >
            Gift
          </button>
        </div>
      </td>
    </tr>
  );
});

export const MyCollectionResults = ({
  items,
  status,
  creditBalance,
  sessionExpired = false,
  onGift,
}: MyCollectionResultsProps) => {
  const [inputValue, setInputValue] = useState("");
  const inputValueRef = useRef(inputValue);
  const [gridFilter, setGridFilter] = useState("");
  const gridFilterRef = useRef(gridFilter);
  const [page, setPage] = useState(1);
  const [sortedBy, setSortedBy] = useState<StickerSortBy>(() =>
    normalizeStickerSortBy(localStorage.getItem("sortedBy"))
  );

  const applyLatestFilter = () => {
    const nextFilter = inputValueRef.current;
    if (gridFilterRef.current === nextFilter) {
      return;
    }
    gridFilterRef.current = nextFilter;
    setGridFilter(nextFilter);
    setPage(1);
  };

  const handleInputChange = (value: string) => {
    inputValueRef.current = value;
    setInputValue(value);
    React.startTransition(() => {
      applyLatestFilter();
    });
  };

  const handleSearch = () => {
    applyLatestFilter();
  };

  const sortedItems = useMemo(
    () => sortOwnedCollectionOnce(items, sortedBy),
    [items, sortedBy]
  );
  const filteredItems = useMemo(
    () => filterOwnedCollection(sortedItems, gridFilter),
    [gridFilter, sortedItems]
  );
  const paged = useMemo(
    () => paginateOwnedCollection(filteredItems, page, OWNED_COLLECTION_PAGE_SIZE),
    [filteredItems, page]
  );
  const rowKeys = useMemo(
    () => getOwnedCollectionRowKeys(paged.items),
    [paged.items]
  );

  useEffect(() => {
    if (page !== paged.page) {
      setPage(paged.page);
    }
  }, [page, paged.page]);

  const resultCountLabel =
    status === "loading"
      ? "Loading collection…"
      : paged.total === 0
        ? gridFilter.trim()
          ? "No stickers match that name."
          : "No stickers in your collection."
        : `Showing ${paged.start}–${paged.end} of ${paged.total}`;

  if (sessionExpired) {
    return (
      <LoungeSignInPrompt
        title="Session expired"
        message="Sign in again to see your sticker collection and credit balance. Refreshing keeps the same expired sign-in."
      />
    );
  }

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-center">
        <h6 className="mid-sub" style={{ fontWeight: "600" }}>
          <span>
            {isKnownNumericCreditBalance(creditBalance)
              ? `Credit Balance : ${creditBalance}`
              : formatLoungeCreditBalance(creditBalance)}
          </span>
        </h6>

        <form
          className="input-group bg-white border rounded-pill p-1"
          style={{ maxWidth: "400px", margin: "20px auto" }}
          onSubmit={(event) => {
            event.preventDefault();
            handleSearch();
          }}
        >
          <input
            type="search"
            placeholder="Search From My Collection"
            aria-label="Search From My Collection"
            aria-describedby="button-addon5"
            className="form-control border-0"
            value={inputValue}
            onChange={(event) => handleInputChange(event.target.value)}
          />
          <div className="input-group-prepend border-0">
            <button
              id="button-addon5"
              type="submit"
              className="btn"
              aria-label="Search collection"
            >
              <i className="fa fa-search"></i>
            </button>
          </div>
        </form>

        <div className="d-flex gap-3 px-1 align-items-center">
          <h6 style={{ fontWeight: "600" }}>Sorted By</h6>
          <div>
            <select
              className="form-select"
              aria-label="Collection sort order"
              value={sortedBy}
              onChange={(event) => {
                const nextSort = normalizeStickerSortBy(event.target.value);
                localStorage.setItem("sortedBy", nextSort);
                setSortedBy(nextSort);
                setPage(1);
              }}
            >
              <option value={"name"}>Name</option>
              <option value={"date"}>Date</option>
            </select>
          </div>
        </div>
      </div>

      <p className="mycollection-result-count" aria-live="polite">
        {resultCountLabel}
      </p>

      <div className="mws-table-main mwstore-table mycollection-table mt-2">
        <div className="table-responsive">
          <table className="table table-borderless">
            <thead>
              <tr className="tab-nav">
                <th style={{ width: "15%", fontWeight: "500" }} scope="col">
                  Image
                </th>
                <th style={{ width: "20%", fontWeight: "500" }} scope="col">
                  Product
                </th>
                <th style={{ width: "25%", fontWeight: "500" }} scope="col">
                  Description
                </th>
                <th style={{ width: "20%", fontWeight: "500" }} scope="col">
                  Status
                </th>
                <th style={{ width: "20%", fontWeight: "500" }} scope="col">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="tab-body">
              {status === "loading"
                ? [...new Array(9)].map((_, index) => (
                    <tr key={`collection-placeholder-${index}`}>
                      <td colSpan={5}>
                        <Placeholder />
                      </td>
                    </tr>
                  ))
                : paged.items.map((item, index) => (
                    <CollectionRow
                      key={rowKeys[index] || String(index)}
                      item={item}
                      onGift={onGift}
                    />
                  ))}
            </tbody>
          </table>
        </div>
      </div>

      {paged.pageCount > 1 && (
        <div className="d-flex justify-content-center gap-3 my-3">
          <button
            type="button"
            className="btn btn-secondary"
            disabled={paged.page <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            Previous
          </button>
          <span>
            Page {paged.page} of {paged.pageCount}
          </span>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={paged.page >= paged.pageCount}
            onClick={() =>
              setPage((current) => Math.min(paged.pageCount, current + 1))
            }
          >
            Next
          </button>
        </div>
      )}
    </>
  );
};
